import { Box, Button, useMediaQuery } from "@mui/material";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { THROW_REACTION_MENU } from "../reactionThrowAssets";
import { usePostHog } from "@posthog/react";

interface Props {
  anchorX: number;
  anchorY: number;
  targetName: string;
  cooldownMs: number;
  onSelect: (reactionId: string) => void;
  onClose: () => void;
}

/** Short viewports (e.g. phone landscape): bottom bar + horizontal scroll so all reactions are reachable. */
function useCompactReactionMenuLayout() {
  const shortByHeight = useMediaQuery("(max-height: 520px)");
  const narrowLandscape = useMediaQuery(
    "(orientation: landscape) and (max-height: 560px) and (max-width: 960px)"
  );
  return shortByHeight || narrowLandscape;
}

export const ReactionMenu = ({ anchorX, anchorY, targetName, cooldownMs, onSelect, onClose }: Props) => {
  const posthog = usePostHog();
  const menuRef = useRef<HTMLDivElement>(null);
  const compact = useCompactReactionMenuLayout();
  const disabled = cooldownMs > 0;

  const [viewport, setViewport] = useState(() => ({
    w: typeof window !== "undefined" ? window.innerWidth : 400,
    h: typeof window !== "undefined" ? window.innerHeight : 800,
  }));

  useEffect(() => {
    const onResize = () => setViewport({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);

  useEffect(() => {
    const handlePointerDown = (e: PointerEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener("pointerdown", handlePointerDown, true);
    return () => document.removeEventListener("pointerdown", handlePointerDown, true);
  }, [onClose]);

  const menuWidth = Math.min(300, viewport.w - 16);
  const pad = 8;
  const { w: vw, h: vh } = viewport;

  const [position, setPosition] = useState({ left: pad, top: pad });

  const computeAnchoredPosition = useCallback(() => {
    const el = menuRef.current;
    const measured = el?.offsetHeight ?? (compact ? 120 : 320);
    const width = Math.min(300, vw - 2 * pad);
    let left = Math.max(pad, Math.min(anchorX - width / 2, vw - width - pad));

    let top = anchorY - measured - 12;
    if (top < pad) {
      top = anchorY + 56;
    }
    const maxTop = Math.max(pad, vh - measured - pad);
    top = Math.min(Math.max(pad, top), maxTop);
    left = Math.max(pad, Math.min(left, vw - width - pad));

    setPosition({ left, top });
  }, [anchorX, anchorY, vw, vh, compact]);

  useLayoutEffect(() => {
    if (compact) return;
    computeAnchoredPosition();
  }, [compact, computeAnchoredPosition]);

  useLayoutEffect(() => {
    if (compact) return;
    const el = menuRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => computeAnchoredPosition());
    ro.observe(el);
    return () => ro.disconnect();
  }, [compact, computeAnchoredPosition]);

  const outerSx = compact
    ? {
        position: "fixed" as const,
        left: pad,
        right: pad,
        bottom: `max(${pad}px, env(safe-area-inset-bottom, 0px))`,
        top: "auto",
        zIndex: 20000,
        width: "auto",
        maxWidth: "100%",
        maxHeight: `min(48dvh, ${vh - 2 * pad}px)`,
        backgroundColor: "rgba(20, 20, 40, 0.97)",
        border: "1px solid rgba(255,255,255,0.15)",
        borderRadius: 2.5,
        overflow: "hidden",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        boxShadow: "0 8px 32px rgba(0,0,0,0.6)",
        display: "flex",
        flexDirection: "column" as const,
      }
    : {
        position: "fixed" as const,
        left: position.left,
        top: position.top,
        zIndex: 20000,
        width: menuWidth,
        maxHeight: `min(calc(100dvh - ${2 * pad}px), calc(100vh - ${2 * pad}px))`,
        backgroundColor: "rgba(20, 20, 40, 0.97)",
        border: "1px solid rgba(255,255,255,0.15)",
        borderRadius: 2.5,
        overflow: "hidden",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        boxShadow: "0 8px 32px rgba(0,0,0,0.6)",
        display: "flex",
        flexDirection: "column" as const,
      };

  return (
    <Box
      ref={menuRef}
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      sx={outerSx}
    >
      <Box
        sx={
          compact
            ? {
                display: "flex",
                flexDirection: "row",
                flexWrap: "nowrap",
                gap: 0.75,
                px: 1,
                py: 0.75,
                overflowX: "auto",
                overflowY: "hidden",
                WebkitOverflowScrolling: "touch",
                overscrollBehaviorX: "contain",
                flex: 1,
                minHeight: 0,
                alignItems: "stretch",
              }
            : {
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: 1,
                p: 1.25,
                pt: 0.5,
                overflowY: "auto",
                overflowX: "hidden",
                WebkitOverflowScrolling: "touch",
                overscrollBehaviorY: "contain",
                flex: 1,
                minHeight: 0,
              }
        }
      >
        {THROW_REACTION_MENU.map((r) => (
          <Button
            key={r.id}
            variant="contained"
            disabled={disabled}
            title={r.label}
            onClick={() => {
              posthog?.capture("reaction_sent", {
                reaction_id: r.id,
                reaction_label: r.label,
                target_name: targetName,
              });
              onSelect(r.id);
            }}
            sx={
              compact
                ? {
                    flex: "0 0 auto",
                    minWidth: 56,
                    maxWidth: 64,
                    py: 0.65,
                    px: 0.25,
                    fontWeight: "bold",
                    fontSize: "0.72rem",
                    lineHeight: 1.2,
                    textTransform: "none",
                    whiteSpace: "normal",
                    background: r.color,
                    color: "#fff",
                    boxShadow: "0 4px 14px rgba(0,0,0,0.35)",
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.25,
                    "&:hover": { background: r.color, filter: "brightness(1.08)" },
                    "&.Mui-disabled": { color: "rgba(255,255,255,0.5)" },
                  }
                : {
                    py: 0.9,
                    px: 0.6,
                    fontWeight: "bold",
                    fontSize: "0.9rem",
                    textTransform: "none",
                    background: r.color,
                    color: "#fff",
                    boxShadow: "0 4px 14px rgba(0,0,0,0.35)",
                    "&:hover": { background: r.color, filter: "brightness(1.08)" },
                    "&.Mui-disabled": { color: "rgba(255,255,255,0.5)" },
                  }
            }
          >
            <Box component="span" sx={{ fontSize: compact ? "1.45rem" : "1.35rem", lineHeight: 1 }}>
              {r.emoji}
            </Box>
          </Button>
        ))}
      </Box>

      {disabled && (
        <Box
          sx={{
            fontSize: "0.55rem",
            color: "#ffc107",
            textAlign: "center",
            pb: 0.75,
            pt: 0,
            flexShrink: 0,
          }}
        >
          Cooldown: {Math.ceil(cooldownMs / 1000)}s
        </Box>
      )}
    </Box>
  );
};
